// ============================================================================
// BP-13-02: Vendor Verification Card Component
// BrightPath Platform - Admin Frontend
// ============================================================================

import React from 'react';
import { cn } from '@/core/lib/utils';
import type { VendorVerification, VendorVerificationStatus } from '@/types/admin';
import {
  useApproveVendor,
  useRejectVendor,
  useRequestVendorInfo,
} from '@/features/admin/hooks/useAdminVendors';

// =============================================================================
// Types
// =============================================================================

interface VendorVerificationCardProps {
  vendor: VendorVerification;
  onViewDetails?: (vendorId: string) => void;
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: VendorVerificationStatus }) {
  const statusConfig = {
    pending: { label: 'Pending Review', className: 'bg-amber-100 text-amber-700' },
    under_review: { label: 'Under Review', className: 'bg-blue-100 text-blue-700' },
    approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
    info_requested: { label: 'Info Requested', className: 'bg-purple-100 text-purple-700' },
  };

  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

// =============================================================================
// Document List
// =============================================================================

function DocumentList({ documents }: { documents: VendorVerification['submittedDocuments'] }) {
  if (!documents || documents.length === 0) {
    return (
      <p className="text-sm italic text-warm-gray">No documents submitted</p>
    );
  }

  return (
    <ul className="space-y-2">
      {documents.map((doc) => (
        <li key={doc.id} className="flex items-center gap-2 text-sm">
          <svg
            className={cn(
              'h-4 w-4',
              doc.verified ? 'text-emerald-500' : 'text-warm-gray'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {doc.verified ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            )}
          </svg>
          <a
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-navy hover:text-sage hover:underline"
          >
            {doc.name}
          </a>
          <span className="text-xs text-warm-gray">({doc.type})</span>
        </li>
      ))}
    </ul>
  );
}

// =============================================================================
// Component
// =============================================================================

export function VendorVerificationCard({ vendor, onViewDetails }: VendorVerificationCardProps) {
  const approveVendor = useApproveVendor();
  const rejectVendor = useRejectVendor();
  const requestInfo = useRequestVendorInfo();

  const handleApprove = () => {
    if (confirm(`Approve ${vendor.businessName} as a verified vendor?`)) {
      approveVendor.mutate(vendor.id);
    }
  };

  const handleReject = () => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      rejectVendor.mutate({ vendorId: vendor.id, reason });
    }
  };

  const handleRequestInfo = () => {
    const message = prompt('What additional information do you need?');
    if (message) {
      requestInfo.mutate({ vendorId: vendor.id, message });
    }
  };

  const isProcessing =
    approveVendor.isPending || rejectVendor.isPending || requestInfo.isPending;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white transition-shadow hover:shadow-md">
      {/* Header */}
      <div className="border-b border-gray-100 bg-cream/50 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {vendor.logoUrl ? (
              <img
                src={vendor.logoUrl}
                alt={vendor.businessName}
                className="h-12 w-12 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-terracotta/10 text-lg font-semibold text-terracotta">
                {vendor.businessName.charAt(0)}
              </div>
            )}
            <div>
              <h3 className="font-semibold text-navy">{vendor.businessName}</h3>
              <p className="text-sm text-warm-gray">{vendor.contactEmail}</p>
            </div>
          </div>
          <StatusBadge status={vendor.status} />
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        {/* Business Info */}
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-warm-gray">
              Category
            </p>
            <p className="mt-1 text-sm text-navy">{vendor.category || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-warm-gray">
              Service Area
            </p>
            <p className="mt-1 text-sm text-navy">
              {vendor.serviceAreaNational ? 'National' : vendor.serviceAreaStates?.join(', ') || '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-warm-gray">
              Years in Business
            </p>
            <p className="mt-1 text-sm text-navy">{vendor.yearsInBusiness || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-warm-gray">
              Submitted
            </p>
            <p className="mt-1 text-sm text-navy">{formatDate(vendor.submittedAt)}</p>
          </div>
        </div>

        {/* Description */}
        {vendor.description && (
          <div className="mb-4">
            <p className="text-xs font-medium uppercase tracking-wide text-warm-gray">
              Description
            </p>
            <p className="mt-1 line-clamp-3 text-sm text-navy">{vendor.description}</p>
          </div>
        )}

        {/* Documents */}
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-warm-gray">
            Verification Documents
          </p>
          <DocumentList documents={vendor.submittedDocuments} />
        </div>

        {/* Admin Notes */}
        {vendor.adminNotes && (
          <div className="mb-4 rounded-lg bg-amber-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-amber-700">
              Admin Notes
            </p>
            <p className="mt-1 text-sm text-amber-800">{vendor.adminNotes}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      {vendor.status !== 'approved' && vendor.status !== 'rejected' && (
        <div className="flex items-center gap-2 border-t border-gray-100 bg-cream/30 px-6 py-3">
          <button
            onClick={() => onViewDetails?.(vendor.id)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-gray-50"
          >
            View Details
          </button>
          <div className="flex-1" />
          <button
            onClick={handleRequestInfo}
            disabled={isProcessing}
            className="rounded-lg bg-purple-100 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-200 disabled:opacity-50"
          >
            {requestInfo.isPending ? '...' : 'Request Info'}
          </button>
          <button
            onClick={handleReject}
            disabled={isProcessing}
            className="rounded-lg bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200 disabled:opacity-50"
          >
            {rejectVendor.isPending ? '...' : 'Reject'}
          </button>
          <button
            onClick={handleApprove}
            disabled={isProcessing}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {approveVendor.isPending ? '...' : 'Approve'}
          </button>
        </div>
      )}
    </div>
  );
}

export default VendorVerificationCard;
